/*
 * TZC - Gateway bridge (client side).
 *
 * WHAT THIS REPLACES
 * There used to be two Gateway implementations that had drifted apart:
 * assets/tzc-forms.js (JSON fetch, media release) and assets/tzc-ambassador.js
 * (raw form POST that navigated the visitor away from the site). Each section
 * also carried its own endpoint setting under a different id, so the URL had to
 * be pasted more than once and could disagree with itself.
 *
 * HOW THIS ONE WORKS
 * Progressive enhancement over the real Shopify form. The markup is always a
 * native {% form %} that works with no JavaScript and no Gateway at all. When
 * an endpoint is configured, this script intercepts the submit, sends a JSON
 * copy to Gateway, and only then decides what to do:
 *
 *   Gateway accepts  -> stop the native submit, show success. One record, in Gateway.
 *   Gateway fails    -> let the native submit through. The data lands in Shopify
 *                       and the store contact inbox instead. Nothing is lost.
 *   Gateway times out-> same as failure. A hung endpoint must not eat a signed
 *                       media release or an ambassador application.
 *   No endpoint set  -> this script does nothing at all.
 *
 * THE RULE THIS ENFORCES: never show a visitor a success message for something
 * that was not delivered anywhere. A silent drop on a media release means we
 * believe we hold a signed permission that does not exist.
 *
 * data-success-url - OPT IN, AND ONLY ON SUCCESS.
 * A form may name a page to send the visitor to instead of showing an inline
 * message. It is read only on the Gateway-accepted branch, so a failed or timed
 * out submit still falls through to the native form exactly as before - a
 * redirect must never be able to stand in for a delivery that did not happen.
 * Forms that do not set the attribute are completely unchanged.
 *
 * =========================================================================
 * VALIDATION HARDENING, 7 Sep 2026
 * =========================================================================
 * Prompted by a free-coffee claim that reached Gateway carrying only an email
 * address, when every field on that form has carried `required` since it was
 * built.
 *
 * WHAT THAT INCIDENT ACTUALLY PROVES. It cannot have come from the site form in
 * a browser. This script calls reportValidity() before it posts, and it is the
 * ONLY path that reaches Gateway - with JavaScript off, the native Shopify
 * submit fires instead and the claim goes to the store inbox as email, never to
 * a webhook. So an incomplete record sitting in Gateway arrived by one of:
 *   - a direct POST to the trigger URL, which is public, unauthenticated, and
 *     will accept any JSON anyone sends it;
 *   - the Gateway-hosted duplicate claim form;
 *   - another form's submissions landing in the wrong workflow.
 * None of those are fixable from a browser. THE ENFORCEMENT HAS TO BE IN THE
 * GATEWAY INTAKE WORKFLOW - branch on the required fields, route anything
 * incomplete to a review stage, and never create a claim from it.
 *
 * The two changes below are defence in depth. They are worth having and they
 * are not the fix.
 *
 * 1. HONEYPOT. A form may carry a field marked data-tzc-hp, hidden off-screen
 *    and out of the tab order. A person never sees it; a form-filling bot that
 *    renders the page fills everything it finds. If it has a value we abort
 *    silently - no error, no native fallback, no clue for the sender that it was
 *    detected, because a bot that learns it failed just tries again differently.
 *    LIMIT WORTH KNOWING: this only catches bots that render the page. A script
 *    POSTing straight at /contact or at the trigger URL never sees the field and
 *    is not stopped by it.
 *
 * 2. THE OLD form.submit() FALLBACK SKIPPED VALIDATION ENTIRELY. On the Gateway
 *    failure branch this script hands over to the native form. requestSubmit()
 *    runs constraint validation the way a real click does; submit() does NOT -
 *    it bypasses required, minlength and type checks completely. The legacy
 *    branch now checks validity first and reports it rather than posting a form
 *    the browser would have refused.
 */
(function () {
  'use strict';

  var TIMEOUT_MS = 8000;

  // contact[First name] -> first_name ; leaves already-clean names alone.
  function keyOf(name) {
    var m = name.match(/^contact\[(.+)\]$/);
    var k = m ? m[1] : name;
    return k.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function payloadFrom(form) {
    var data = {};
    new FormData(form).forEach(function (value, name) {
      if (name === 'form_type' || name === 'utf8') return;
      var k = keyOf(name);
      if (!k || k === 'tzc_hp') return; // never forward the trap field
      if (Object.prototype.hasOwnProperty.call(data, k)) {
        if (!Array.isArray(data[k])) data[k] = [data[k]];
        data[k].push(value);
      } else {
        data[k] = value;
      }
    });
    data.form_source = form.getAttribute('data-form-source') || 'Till Zero website';
    data.page_url = window.location.href;
    data.submitted_at = new Date().toISOString();
    return data;
  }

  function post(endpoint, payload) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, TIMEOUT_MS);
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller ? controller.signal : undefined,
      mode: 'cors',
      keepalive: true
    }).then(function (res) {
      clearTimeout(timer);
      // An opaque response (no CORS headers) still means the request left the
      // browser and Gateway received it. Treat it as delivered.
      if (res.type === 'opaque') return true;
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return true;
    }).catch(function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-tzc-gateway]');
    if (!form) return;
    if (form.hasAttribute('data-tzc-passthrough')) return; // native fallback in progress

    // Honeypot. Checked before anything else, and before the endpoint test, so
    // it protects the native path too. Silent on purpose.
    var hp = form.querySelector('[data-tzc-hp]');
    if (hp && hp.value !== '') {
      event.preventDefault();
      return;
    }

    var endpoint = form.getAttribute('data-endpoint');
    if (!endpoint) return; // not configured - native form handles everything

    event.preventDefault();
    if (form.reportValidity && !form.reportValidity()) return;

    var status = form.querySelector('[data-form-status]');
    var button = form.querySelector('[type="submit"]');
    if (status) { status.hidden = false; status.textContent = 'Sending\u2026'; }
    if (button) button.disabled = true;

    post(endpoint, payloadFrom(form)).then(function () {
      // Delivered. If this form names a confirmation page, go there.
      var go = form.getAttribute('data-success-url');
      if (go) {
        // The button stays disabled through the navigation, so an impatient
        // second click during the page swap cannot fire a duplicate claim.
        form.reset();
        if (status) { status.hidden = false; status.textContent = 'Sent. Taking you to what happens next\u2026'; }
        window.location.assign(go);
        return;
      }
      if (button) button.disabled = false;
      form.reset();
      if (status) {
        status.hidden = false;
        status.textContent = form.getAttribute('data-success') || 'Received. We will be in touch.';
        if (status.focus) status.focus();
      }
    }).catch(function () {
      // Gateway is unreachable, slow or erroring. Do NOT tell the visitor it
      // worked and do NOT drop the submission - hand it to the native Shopify
      // form, which emails the store and creates the record there. This branch
      // never reads data-success-url: the native submit lands on the form's own
      // page, and that page decides where to send them next.
      form.setAttribute('data-tzc-passthrough', '');

      if (typeof form.requestSubmit === 'function') {
        // Runs constraint validation, exactly like a real click.
        if (status) { status.hidden = false; status.textContent = 'Sending\u2026'; }
        form.requestSubmit();
        return;
      }

      // Legacy path. form.submit() does NOT validate, so check first rather
      // than posting something the browser would have refused.
      if (form.checkValidity && !form.checkValidity()) {
        form.removeAttribute('data-tzc-passthrough');
        if (button) button.disabled = false;
        if (form.reportValidity) form.reportValidity();
        if (status) { status.hidden = false; status.textContent = 'Please complete every field and try again.'; }
        return;
      }
      if (status) { status.hidden = false; status.textContent = 'Sending\u2026'; }
      form.submit();
    });
  });
})();
